/* FRAG SUPPLY — original storefront behaviour. Native Shopify forms are the baseline. */
(() => {
  'use strict';
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const cartURL = document.body.dataset.cartUrl || '/cart';
  let lastDialogTrigger = null;
  let cartBusy = false;
  const announce = message => { const status = $('[data-announcer]'); if (status) status.textContent = message; };
  const showError = (root, selector, message) => { const el = $(selector, root); if (el) { el.hidden = false; el.textContent = message; } };
  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {credentials: 'same-origin', ...options, headers: {'Accept': 'application/json', ...(options.headers || {})}});
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'Something went wrong. Please try again.');
    return data;
  }
  function openDialog(dialog, trigger) {
    if (!dialog || dialog.open) return;
    lastDialogTrigger = trigger || document.activeElement;
    dialog.showModal();
    document.body.classList.add('dialog-open');
  }
  function closeDialog(dialog) {
    if (!dialog) return;
    dialog.close();
    document.body.classList.remove('dialog-open');
    if (lastDialogTrigger?.isConnected) lastDialogTrigger.focus();
  }
  function bindDialog(dialog) {
    if (!dialog || dialog.dataset.bound) return;
    dialog.dataset.bound = 'true';
    dialog.addEventListener('close', () => { document.body.classList.remove('dialog-open'); if (lastDialogTrigger?.isConnected) lastDialogTrigger.focus(); });
    dialog.addEventListener('click', event => { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) closeDialog(dialog); } });
  }
  async function refreshCart(shouldOpen = false, trigger = null) {
    const [cart, sections] = await Promise.all([requestJSON(`${cartURL}.js`), requestJSON(`${cartURL}?sections=cart-drawer`)]);
    if (!sections['cart-drawer']) throw new Error('Your bag was updated. Open your bag to continue.');
    const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const fresh = $('#CartDrawer', doc), current = $('#CartDrawer');
    if (!fresh || !current) throw new Error('Your bag was updated. Open your bag to continue.');
    // Keep the same dialog element so its modal state and focus trap survive updates.
    current.innerHTML = fresh.innerHTML;
    $$('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
    if (shouldOpen) openDialog(current, trigger);
    announce(`Your bag contains ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}.`);
    return current;
  }
  function showMedia(root, id) {
    if (!$(`[data-media-panel="${CSS.escape(String(id))}"]`, root)) return;
    $$('[data-media-panel]', root).forEach(panel => { panel.hidden = panel.dataset.mediaPanel !== String(id); if (panel.hidden) $$('video', panel).forEach(video => video.pause()); });
    $$('[data-media-select]', root).forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mediaSelect === String(id))));
  }
  function separateDisclaimer(section) {
    const description = $('[data-fragrance-description]', section);
    const accordion = $('[data-product-disclaimer]', section);
    const content = $('[data-disclaimer-content]', accordion || section);
    if (!description || !accordion || !content) return;
    // Liquid handles the catalogue's existing heading. Allow rich-text editor
    // variations too, without matching the word "disclaimer" inside normal prose.
    const heading = $$('p, h1, h2, h3, h4, h5, h6', description)
      .find(node => /^disclaimer\s*:?$/i.test(node.textContent.trim()));
    if (heading) {
      const range = document.createRange();
      range.setStartAfter(heading);
      range.setEnd(description, description.childNodes.length);
      if (range.toString().trim()) content.replaceChildren(range.extractContents());
      heading.remove();
      accordion.hidden = !content.textContent.trim();
    }
    // Remove spacer paragraphs left behind by the old trailing disclaimer.
    while (description.lastChild) {
      const node = description.lastChild;
      if (node.textContent.trim() || (node.nodeType === 1 && !/^(P|DIV|SPAN|BR)$/.test(node.tagName)) || node.querySelector?.('img, video, iframe, svg, input')) break;
      node.remove();
    }
  }
  function bindProduct(section) {
    if (section.dataset.bound) return;
    section.dataset.bound = 'true';
    separateDisclaimer(section);
    const form = $('.product-form', section), data = $('[data-variant-data]', section);
    if (!form || !data) return;
    let variants;
    try { variants = JSON.parse(data.textContent); } catch { return; }
    form.addEventListener('change', event => {
      if (event.target.name !== 'id') return;
      const variant = variants.find(item => String(item.id) === event.target.value);
      if (!variant) return;
      $('[data-product-price]', section).textContent = variant.price;
      const compare = $('[data-compare-price]', section); compare.hidden = !variant.compare; compare.textContent = variant.compare || '';
      $('[data-unit-price]', section).textContent = variant.unit || '';
      const button = $('[data-add-button]', section); button.disabled = !variant.available; button.textContent = variant.available ? 'Add to bag' : 'Sold out';
      const quantity = $('[name="quantity"]', form); quantity.min = variant.min || 1; quantity.step = variant.step || 1;
      if (variant.max != null) quantity.max = variant.max; else quantity.removeAttribute('max');
      quantity.value = variant.min || 1;
      $('[data-product-error]', section).hidden = true;
      const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
      if (variant.media) showMedia(section, variant.media);
      section.dispatchEvent(new CustomEvent('variant:change', {bubbles:true, detail:{variant}}));
    });
    form.addEventListener('submit', async event => {
      if (event.submitter?.name !== 'add') return;
      event.preventDefault();
      const button = $('[data-add-button]', form);
      if (button.disabled || cartBusy) return;
      const label = button.textContent; button.disabled = true; button.textContent = 'Adding…'; form.setAttribute('aria-busy', 'true');
      const error = $('[data-product-error]', section); error.hidden = true;
      let added = false;
      try {
        await requestJSON(`${document.body.dataset.cartAddUrl || '/cart/add'}.js`, {method:'POST', body:new FormData(form)});
        added = true;
        await refreshCart(true, button);
      } catch (err) {
        if (added) { location.assign(cartURL); return; }
        showError(section, '[data-product-error]', err.message || 'Unable to add this fragrance. Please try again.');
      } finally { button.disabled = false; button.textContent = label; form.removeAttribute('aria-busy'); }
    });
  }
  async function changeLine(key, quantity, trigger) {
    if (cartBusy) return;
    cartBusy = true;
    const dialog = $('#CartDrawer');
    dialog?.setAttribute('aria-busy', 'true');
    try {
      await requestJSON(`${cartURL}/change.js`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:key,quantity})});
      const root = await refreshCart();
      const field = $(`[data-line-key="${CSS.escape(key)}"]`, root);
      (field || $('[data-close-dialog]', root))?.focus();
    } catch (err) { showError(dialog || document, '[data-cart-error]', err.message); trigger?.focus(); }
    finally { cartBusy = false; dialog?.removeAttribute('aria-busy'); }
  }
  document.addEventListener('click', event => {
    const step = event.target.closest('[data-quantity-step]');
    if (step) {
      const input = $('input', step.closest('.quantity-control'));
      if (cartBusy || !input) return;
      const min = Number(input.min || 0), max = input.max === '' ? Infinity : Number(input.max), increment = Number(input.step || 1);
      const next = Math.min(max, Math.max(min, Number(input.value || min) + Number(step.dataset.quantityStep) * increment));
      input.value = next; input.dispatchEvent(new Event('change', {bubbles:true})); return;
    }
    const cartTrigger = event.target.closest('[data-open-cart]');
    if (cartTrigger && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0 && $('#CartDrawer')?.showModal) {
      event.preventDefault(); refreshCart(true, cartTrigger).catch(() => location.assign(cartURL)); return;
    }
    const close = event.target.closest('[data-close-dialog]');
    if (close) { closeDialog(close.closest('dialog')); return; }
    const media = event.target.closest('[data-media-select]');
    if (media) { showMedia(media.closest('[data-gallery]'), media.dataset.mediaSelect); return; }
    const zoom = event.target.closest('[data-image-zoom]');
    if (zoom && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      let dialog = $('#ImageDialog');
      if (!dialog) { dialog = document.createElement('dialog'); dialog.id = 'ImageDialog'; dialog.className = 'image-dialog'; dialog.setAttribute('aria-label', 'Enlarged product image'); const closeButton = document.createElement('button'); closeButton.type = 'button'; closeButton.dataset.closeDialog = ''; closeButton.className = 'button'; closeButton.textContent = 'Close image'; const img = document.createElement('img'); dialog.append(closeButton, img); document.body.append(dialog); bindDialog(dialog); }
      const img = $('img', dialog); img.src = zoom.href; img.alt = $('img', zoom)?.alt || 'Product image'; openDialog(dialog, zoom); return;
    }
    const remove = event.target.closest('[data-remove-line]');
    if (remove?.closest('#CartDrawer')) { event.preventDefault(); changeLine(remove.dataset.removeLine, 0, remove); return; }
    if (event.target.closest('[data-print]')) window.print();
  });
  document.addEventListener('change', event => {
    const target = event.target;
    if (target.matches('[data-cart-quantity]') && target.closest('#CartDrawer')) {
      const value = Number(target.value);
      if (!Number.isSafeInteger(value) || value < 0) { target.reportValidity(); return; }
      changeLine(target.dataset.lineKey, value, target);
    }
    if (target.matches('[data-sort-by]')) { const url = new URL(location.href); url.searchParams.set('sort_by', target.value); url.searchParams.delete('page'); location.assign(url); }
  });
  document.addEventListener('keydown', event => { if (event.key === 'Escape') $$('[data-mobile-menu][open],.nav-dropdown[open],.filter-disclosure[open]').forEach(details => { details.open = false; $('summary', details)?.focus(); }); });
  document.addEventListener('click', event => { $$('.nav-dropdown[open],[data-mobile-menu][open]').forEach(details => { if (!details.contains(event.target)) details.open = false; }); });
  function loadRecommendations(root = document) {
    $$('[data-recommendations]', root).forEach(async el => {
      if (el.dataset.loaded || !el.dataset.url) return;
      el.dataset.loaded = 'true';
      try { const response = await fetch(el.dataset.url, {credentials:'same-origin'}); if (!response.ok) return; const doc = new DOMParser().parseFromString(await response.text(), 'text/html'); const content = $('[data-recommendations]', doc); if (content) el.innerHTML = content.innerHTML; } catch { /* Recommendations are optional. */ }
    });
  }
  function bindAddresses(root = document) {
    $$('[data-country-select]', root).forEach(select => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true'; select.value = select.dataset.default;
      const province = document.getElementById(select.dataset.provinceId);
      const update = () => {
        let provinces = []; try { provinces = JSON.parse(select.selectedOptions[0]?.dataset.provinces || '[]'); } catch { /* Native country value remains valid. */ }
        province.replaceChildren(); provinces.forEach(([value, label]) => province.add(new Option(label, value)));
        province.closest('[data-province-container]').hidden = provinces.length === 0; province.disabled = provinces.length === 0;
        if ([...province.options].some(option => option.value === province.dataset.default)) province.value = province.dataset.default;
      }; select.addEventListener('change', update); update();
    });
  }
  $$('[data-delete-address]').forEach(form => form.addEventListener('submit', event => { if (!window.confirm('Delete this address?')) event.preventDefault(); }));
  function policyContents() {
    const container = $('.shopify-policy__container'); if (!container || $('.policy-toc', container)) return;
    const body = $('.shopify-policy__body', container), title = $('.shopify-policy__title h1', container);
    if (!body) return;
    const duplicate = $('h1', body);
    if (duplicate && title && duplicate.textContent.trim().toLowerCase() === title.textContent.trim().toLowerCase()) duplicate.hidden = true;
    const headings = $$('h2,h3', body).filter(heading => heading.textContent.trim()); if (headings.length < 4) return;
    const details = document.createElement('details'); details.className = 'policy-toc'; const summary = document.createElement('summary'); summary.textContent = 'On this page'; const list = document.createElement('ol');
    headings.forEach((heading,index) => { if (!heading.id) heading.id = `policy-section-${index+1}`; const li = document.createElement('li'), a = document.createElement('a'); a.href = `#${heading.id}`; a.textContent = heading.textContent; li.append(a); list.append(li); });
    details.append(summary,list); body.before(details);
  }
  function init(root = document) { $$('[data-product-section]', root).forEach(bindProduct); $$('dialog', root).forEach(bindDialog); loadRecommendations(root); bindAddresses(root); policyContents(); }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('pageshow', event => { if (event.persisted && $('#CartDrawer')) refreshCart().catch(() => {}); });
})();
